import JsonView from 'react18-json-view';
import { debounce, pick } from 'radash';
import { useRef, useEffect, useState } from 'react';
import { FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { IoCloseCircle } from 'react-icons/io5';
import { MdAccessTime, MdError } from 'react-icons/md';
import { useJobData } from '../../state';
import { Loading, Button } from '../../components';
import { PillButtons } from '../input';
import { Modal } from '../../components/modal';
import { Error } from '../../components/error';
import { TableFromObject } from '../../components/table';
import { TimesChart } from '../chart';
import { Timer } from './Timer';
import { Cost } from './Cost';
import { VisitBadge } from '../badge';
import { colors, styles } from '../../constants';

const border = '1px solid #ccc';
const headerStyle = { ...styles.smallCaps, color: '#888' };

export const Percent = ({ percent }) => {
  if (percent === undefined) {
    return null;
  }

  const p = percent * 100;

  const height = 24;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          marginTop: 4,
          width: '100%',
          background: '#ccc',
          borderRadius: 4,
          height,
        }}
      >
        <div
          style={{
            color: 'white',
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            fontSize: 12,
            width: '100%',
            lineHeight: height + 'px',
          }}
        >
          {!!p && <span>{Math.floor(p)}%</span>}
          {!p && <Loading size="xs" color="gray" />}
        </div>
        <div
          style={{
            width: `${p}%`,
            background: colors.green,
            height: '100%',
            borderRadius: 4,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

export const TimesChartForJobs = ({ jobs }) => {
  const [times, setTimes] = useState([]);

  useEffect(() => {
    const times_ = [];
    for (const job of jobs) {
      times_.push({
        id: job.jobId,
        state: job.state,
        started: job.started,
        finished: job.finished,
        label: job.url || job.name,
      });
    }
    setTimes(times_);
  }, [jobs]);

  return (
    <div>
      <TimesChart times={times} />
    </div>
  );
};

const ChildJobs = ({ jobs }) => {
  const [expanded, setExpanded] = useState();
  const [visible, setVisible] = useState([]);
  const [tick, setTick] = useState(0);
  const [opened, setOpened] = useState();
  const [now, setNow] = useState(new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
      setNow(new Date().getTime());
    }, 88);
    return () => clearInterval(interval);
  }, []);

  const cutoff = 20;

  useEffect(() => {
    setVisible([...(jobs || [])].sort((a, b) => b.started - a.started).slice(0, expanded ? jobs.length : cutoff));
  }, [jobs, tick, expanded]);

  const open = (jobId) => {
    setOpened(opened == jobId ? null : jobId);
  };

  const fontSize = 10;

  const nodes = visible.map((job, idx) => {
    const finished = !!job.finished;
    const msec = Math.max(job.started, job.finished || now) - job.started;
    const sharedStyle = { marginRight: 4 };

    const iconSize = 12;

    const icon = {
      waiting: <MdAccessTime size={iconSize} />,
      delayed: <MdAccessTime size={iconSize} />,
      paused: <MdAccessTime size={iconSize} />,
      'waiting-children': <MdAccessTime />,
      active: <Loading size="xs" />,
      completed: <FaCheckCircle size={iconSize} color={colors.green} />,
      failed: <MdError size={iconSize + 2} color={colors.error} />,
    }[job?.state];

    const activeStyle = {
      background: colors.lightBlue,
    };

    let ok;
    if (['visit', 'visitInner'].includes(job.name)) {
      ok = (job.statuses || []).map((it) => (it == 'ok' ? null : <VisitBadge status={it} />)).filter(Boolean);
      if (ok.length == 0) {
        ok = null;
      }
    }

    return (
      <div
        key={idx}
        style={{
          display: 'flex',
          alignItems: 'center',
          borderTop: idx ? '1px solid #eee' : '',
          padding: 0,
          gap: 2,
          cursor: 'pointer',
          ...(opened == job.jobId ? activeStyle : {}),
        }}
        onClick={() => open(job.jobId)}
      >
        <div style={{ ...sharedStyle, paddingLeft: 2, width: 18, textAlign: 'center' }}>{icon}</div>
        {ok && <div style={{ ...sharedStyle, paddingLeft: 2, textAlign: 'center' }}>{ok}</div>}
        <div style={{ ...sharedStyle, width: 40, fontSize, fontFamily: 'monospace', textAlign: 'right' }}>
          {(msec / 1e3).toFixed(1)}s
        </div>
        <div style={{ ...sharedStyle, width: 100, fontSize, textAlign: 'left' }}>{job.name}</div>

        <div style={{ ...sharedStyle, width: 80, fontSize, fontFamily: 'monospace', textAlign: 'left' }}>
          {job.jobId}
        </div>
        {/*
         */}
        <div style={{ ...sharedStyle, ...styles.textOverflowStyle, fontSize, width: '100%' }}>{job.url}</div>
      </div>
    );
  });

  const borderRadius = '4px';

  const showMoreNode = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        background: 'white',
        borderTop: border,
        position: 'sticky',
        padding: 5,
        bottom: 0,
        zIndex: 5,
        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
      }}
    >
      <Button trans small gray onClick={() => setExpanded(true)}>
        Show All {jobs.length} Jobs
      </Button>
    </div>
  );

  const showLessNode = (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        borderTop: border,
        background: 'white',
        padding: 5,
        display: 'flex',
        justifyContent: 'center',
        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
      }}
    >
      <Button trans small gray onClick={() => setExpanded(false)}>
        Collapse
      </Button>
    </div>
  );

  const openedNode = opened && <JobDetails jobId={opened} onClose={() => setOpened(null)} />;

  const maxHeight = expanded ? 600 : 400;

  return (
    <div>
      {/*
      <pre>{JSON.stringify(jobs, null, 2)}</pre>
      */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, overflow: 'hidden' }}>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius,
            maxHeight,
            overflowY: expanded ? 'scroll' : 'hidden',
            width: opened ? '30%' : '100%',
          }}
        >
          {nodes}
          {!expanded && jobs.length > cutoff && showMoreNode}
          {expanded && showLessNode}
        </div>

        {openedNode && (
          <div style={{ width: '70%', border: '1px solid #ccc', borderRadius, maxHeight, overflowY: 'scroll' }}>
            {openedNode}
          </div>
        )}
      </div>
    </div>
  );
};

const JobDetails = ({ jobId, onClose }) => {
  const [tab, setTab] = useState('pretty');
  const { job, loading } = useJobData(jobId);

  if (!job?.method) {
    return (
      <div style={{ padding: 10 }}>
        <Loading>Loading {job?.id}...</Loading>
      </div>
    );
  }

  let cls = {
    visit: JobDetailsVisit,
    visitInner: JobDetailsVisit,
    extractBatch: JobDetailsVisit,
    runCompletion: JobDetailsRunCompletion,
  }[job.method];

  const fields = {
    visit: ['url', 'urls', 'proxy', 'include'],
    visitInner: ['url', 'urls', 'proxy', 'include'],
    extractBatch: ['url', 'urls', 'template', 'perPage'],
  }[job.method];

  let hasCustom = Boolean(cls);
  if (!cls) {
    cls = JobDetailsJson;
  }

  const choices = [
    ['pretty', 'Pretty'],
    ['json', 'JSON'],
  ];
  const tabs = hasCustom ? <PillButtons value={tab} onChange={setTab} choices={choices} /> : null;

  const prettyNode = cls({ job, fields });
  const jsonNode = <JobDetailsJson job={job} />;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: 'white',
          borderRadius: '4px 4px 0 0',
          borderBottom: border,
          padding: '5px 10px',
          zIndex: 2,
        }}
      >
        <div>
          <b>
            {job.method} {job.id}
            {job.state && `: ${job.state}`}
          </b>
        </div>
        <IoCloseCircle style={{ cursor: 'pointer' }} size={24} onClick={onClose} />
      </div>

      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tabs}
        {tab == 'pretty' && prettyNode}
        {tab == 'json' && jsonNode}
      </div>
    </div>
  );
};

const JobDetailsJson = ({ job }) => {
  return (
    <div style={{ overflow: 'hidden', fontSize: 12 }}>
      <JsonView src={job} />
    </div>
  );
};

const JobDetailsRunCompletion = ({ job }) => {
  const promptStyle = {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: 12,
    overflow: 'hidden',
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: 10,
  };

  return (
    <div>
      <h3>Prompt</h3>
      <Modal>
        <div style={{ ...promptStyle, maxHeight: 200 }}>{job.params?.prompt}</div>
        <div style={{ ...promptStyle, width: '100%', maxWidth: 800 }}>{job.params?.prompt}</div>
      </Modal>

      <h3>Answer</h3>
      <div style={{ ...promptStyle }}>{JSON.stringify(job?.answer?.partial, null, 2)}</div>

      <h3>Cost</h3>
      <Cost job={job} expanded />

      {/*
      <pre>{JSON.stringify(job.answer, null, 2,)}</pre>
      */}
    </div>
  );
};

const JobDetailsVisit = ({ job, fields }) => {
  const fontSize = 12;
  if (!job) {
    return null;
  }

  const urls = [];
  if (job.params?.url) {
    urls.push(job.params.url);
  }
  if (job.params?.urls) {
    urls.push(...job.params.urls);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={headerStyle}>URL{urls.length == 1 ? '' : 's'}</div>
        {urls.map((url) => (
          <a style={{ fontWeight: 'bold', fontSize: 13 }} href={url} target="_blank">
            {url}
          </a>
        ))}
      </div>

      <div>
        <div style={headerStyle}>Parameters</div>
        <TableFromObject obj={pick(job.params, fields)} />
      </div>

      <div>
        <Error message={job.error} style={{ marginTop: 10 }} />
      </div>

      {Array.isArray(job.results?.items) && (
        <div>
          <div style={headerStyle}>Items</div>
          {job.results.items.map((it) => (
            <TableFromObject obj={it} />
          ))}
        </div>
      )}

      {!Array.isArray(job.results?.items) && (
        <div>
          <div style={headerStyle}>Results</div>
          {Array.isArray(job.results) && job.results.map((it) => <TableFromObject obj={it} />)}
          {!Array.isArray(job.results) && <TableFromObject obj={job.results} />}
        </div>
      )}

      <div>
        <div style={headerStyle}>Cost</div>
        <Cost job={job} expanded />
      </div>

      <div>
        <div style={headerStyle}>Timer</div>
        <Timer timer={job.timer} />
      </div>
    </div>
  );
};

export const Progress = ({ job, slim }) => {
  if (!job) {
    return null;
  }

  const progress = job.progress;

  const children = (job?.children || []).sort((a, b) => a.started - b.started);
  const percent = (progress?.progress || 0) / (progress?.total || 1);

  const jobsListNode = <ChildJobs jobs={children} />;

  const chart = <TimesChartForJobs jobs={children} />;
  const chartNode = slim ? (
    <Modal title={`Job ${job?.id} progress`}>
      {chart}
      <div style={{ width: '100%' }}>{jobsListNode}</div>
    </Modal>
  ) : (
    chart
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Boolean(progress?.progress) && percent < 1 && <Percent state={job.state} percent={percent} />}

      {children.length > 0 && <div style={{ background: 'white' }}>{chartNode}</div>}
      {!slim && <div style={{ background: 'white' }}>{jobsListNode}</div>}
    </div>
  );
};
